// FileGalleryFullScreenNew - SLEEK & MODERN Design
import React, { useState, useEffect } from 'react';
import { 
  X, Save, Trash2, FileText, Camera, Upload, Image as ImageIcon, 
  File, Plus, Activity, MapPin, Calendar, Users, User, Clock,
  Building2, Briefcase, AlertCircle, CheckCircle, ArrowLeft, Eye, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';
import FormsList from './forms/FormsList';

const FileGalleryFullScreenNew = ({ 
  isOpen, 
  onClose, 
  record, 
  recordType,
  onDelete,
  onUpdate
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [forms, setForms] = useState([]);

  useEffect(() => {
    if (record) {
      fetchPhotos();
      fetchDocuments();
      fetchForms();
    }
  }, [record]);

  const fetchPhotos = async () => {
    try {
      const response = await api.get(`/${recordType}s/${record.id}/photos`);
      setPhotos(response.data || []);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await api.get(`/${recordType}s/${record.id}/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const fetchForms = async () => {
    try {
      const response = await api.get(`/${recordType}s/${record.id}/forms`);
      setForms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    
    try {
      await api.delete(`/${recordType}s/${record.id}`);
      toast.success('Deleted successfully');
      if (onDelete) onDelete();
      onClose();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      await api.post(`/${recordType}s/${record.id}/photos`, formData);
      toast.success('Photos uploaded');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to upload photos');
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      await api.post(`/${recordType}s/${record.id}/documents`, formData);
      toast.success('Documents uploaded');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to upload documents');
    }
  };

  // Get priority badge - SLEEK design
  const getPriorityBadge = (priority) => {
    const priorityMap = {
      high: { color: 'bg-red-500/10 text-red-400 border-red-500/30', label: 'High' },
      medium: { color: 'bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30', label: 'Medium' },
      low: { color: 'bg-gray-500/10 text-gray-400 border-gray-500/30', label: 'Low' }
    };
    
    const config = priorityMap[priority?.toLowerCase()] || priorityMap.medium;
    
    return (
      <Badge className={`${config.color} border text-xs px-2 py-0.5`}>
        {config.label}
      </Badge>
    );
  };

  // Get status badge - SLEEK design
  const getStatusBadge = (status) => {
    const statusMap = {
      'to do': { color: 'bg-gray-500/10 text-gray-400 border-gray-500/30', label: 'To Do' },
      'in progress': { color: 'bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30', label: 'In Progress' },
      'completed': { color: 'bg-[#C9A961]/10 text-[#C9A961] border-green-500/30', label: 'Completed' },
      'active': { color: 'bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30', label: 'Active' },
      'inactive': { color: 'bg-gray-500/10 text-gray-400 border-gray-500/30', label: 'Inactive' }
    };
    
    const config = statusMap[status?.toLowerCase()] || statusMap['to do'];
    
    return (
      <Badge className={`${config.color} border text-xs px-2 py-0.5`}>
        {config.label}
      </Badge>
    );
  };

  // Get type badge - SLEEK design
  const getTypeBadge = () => {
    if (recordType === 'workorder') {
      return (
        <Badge className="bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30 border text-sm px-3 py-1">
          <Building2 className="h-3.5 w-3.5 mr-1.5" />
          WORK ORDER
        </Badge>
      );
    } else if (recordType === 'task') {
      const locationType = record?.location_type || 'field';
      const isField = locationType === 'field';
      
      return (
        <Badge className={`${isField ? 'bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30' : 'bg-gray-500/10 text-gray-400 border-gray-500/30'} border text-sm px-3 py-1`}>
          {isField ? <Building2 className="h-3.5 w-3.5 mr-1.5" /> : <Briefcase className="h-3.5 w-3.5 mr-1.5" />}
          {isField ? 'FIELD TASK' : 'OFFICE TASK'}
        </Badge>
      );
    } else {
      const projectType = record?.project_type || 'field';
      const isField = projectType === 'field';
      
      return (
        <Badge className={`${isField ? 'bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30' : 'bg-gray-500/10 text-gray-400 border-gray-500/30'} border text-sm px-3 py-1`}>
          {isField ? <Building2 className="h-3.5 w-3.5 mr-1.5" /> : <Briefcase className="h-3.5 w-3.5 mr-1.5" />}
          {isField ? 'FIELD PROJECT' : 'OFFICE PROJECT'}
        </Badge>
      );
    }
  };

  // Get project badge for tasks
  const getProjectBadge = () => {
    if (recordType === 'task' && record?.is_project_task && record?.project_name) {
      return (
        <Badge className="bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30 border text-xs px-2 py-0.5">
          <Building2 className="h-3 w-3 mr-1" />
          {record.project_name}
        </Badge>
      );
    }
    return null;
  };

  // Get deadline urgency
  const getDeadlineUrgency = (deadline) => {
    if (!deadline) return '';
    
    const deadlineDate = new Date(deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadlineDate.setHours(0, 0, 0, 0);
    
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return <span className="text-red-400 font-medium ml-2 text-sm">(Overdue!)</span>;
    if (diffDays === 0) return <span className="text-[#C9A961] font-medium ml-2 text-sm">(Today!)</span>;
    if (diffDays === 1) return <span className="text-[#C9A961] ml-2 text-sm">(Tomorrow!)</span>;
    
    return '';
  };

  if (!isOpen || !record) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
        {/* Minimal Header */}
        <div className="sticky top-0 z-40 bg-black/80 backdrop-blur-sm border-b border-[#C9A961]/20 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-[#C9A961] hover:bg-[#C9A961]/10 h-8"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back
            </Button>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-[#C9A961] hover:bg-[#C9A961]/10 h-8 text-xs"
              >
                <Save className="h-3.5 w-3.5 mr-1.5" />
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-400 hover:bg-red-500/10 h-8 text-xs"
                onClick={handleDelete}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Content Area - SLEEK */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Title Section - Minimal */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-2">
              {getTypeBadge()}
              {recordType === 'workorder' && record?.work_order_number && (
                <Badge className="bg-black/50 border border-[#C9A961]/30 text-[#C9A961] text-xs px-2 py-0.5 font-mono">
                  #{record.work_order_number}
                </Badge>
              )}
            </div>
            
            <h1 className="text-3xl font-bold text-white">
              {record?.title || record?.name || 'Untitled'}
            </h1>

            <div className="flex items-center gap-2">
              {recordType === 'task' && record?.priority && getPriorityBadge(record.priority)}
              {recordType === 'workorder' && record?.priority && getPriorityBadge(record.priority)}
              {getStatusBadge(record?.status)}
              {getProjectBadge()}
            </div>
          </div>

          {/* Minimal Tabs */}
          <div className="flex gap-1 mb-6 border-b border-[#C9A961]/20">
            {['details', 'forms', 'photos', 'documents'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab
                    ? 'text-[#C9A961] border-[#C9A961]'
                    : 'text-gray-500 border-transparent hover:text-gray-300'
                }`}
              >
                {tab === 'details' && <FileText className="h-4 w-4 inline mr-1.5" />}
                {tab === 'forms' && <File className="h-4 w-4 inline mr-1.5" />}
                {tab === 'photos' && <ImageIcon className="h-4 w-4 inline mr-1.5" />}
                {tab === 'documents' && <File className="h-4 w-4 inline mr-1.5" />}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'forms' && ` (${forms.length})`}
                {tab === 'photos' && ` (${photos.length})`}
                {tab === 'documents' && ` (${documents.length})`}
              </button>
            ))}
          </div>

          {/* Tab Content - CLEAN & MINIMAL */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {record?.address && (
                <div>
                  <div className="flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-wider mb-2">
                    <MapPin className="h-4 w-4" />
                    Address
                  </div>
                  <p className="text-white text-base">{record.address}</p>
                </div>
              )}

              {record?.deadline && (
                <div>
                  <div className="flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-wider mb-2">
                    <Calendar className="h-4 w-4" />
                    Deadline
                  </div>
                  <p className="text-white text-base">
                    {new Date(record.deadline).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                    {getDeadlineUrgency(record.deadline)}
                  </p>
                </div>
              )}

              {record?.description && (
                <div>
                  <div className="flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-wider mb-2">
                    <FileText className="h-4 w-4" />
                    Description
                  </div>
                  <p className="text-white text-base leading-relaxed">{record.description}</p>
                </div>
              )}

              {record?.assigned_users && record.assigned_users.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-wider mb-2">
                    <Users className="h-4 w-4" />
                    Team
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {record.assigned_users.map((user, index) => (
                      <Badge 
                        key={index} 
                        className="bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30 border text-xs px-2 py-0.5"
                      >
                        <User className="h-3 w-3 mr-1" />
                        {user.username || user.name || 'Unknown'}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {recordType === 'task' && record?.location_type === 'office' && record?.department && (
                <div>
                  <div className="flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-wider mb-2">
                    <Briefcase className="h-4 w-4" />
                    Department
                  </div>
                  <p className="text-white text-base">{record.department}</p>
                </div>
              )}

              {record?.created_by_name && (
                <div className="pt-4 border-t border-[#C9A961]/10">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                    <User className="h-3.5 w-3.5" />
                    Created by
                  </div>
                  <p className="text-gray-400 text-sm">
                    {record.created_by_name} • {record.created_at ? new Date(record.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown'}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'forms' && (
            <div>
              <FormsList
                recordType={recordType}
                recordId={record?.id}
                canEdit={true}
              />
            </div>
          )}

          {activeTab === 'photos' && (
            <div>
              <div className="flex gap-2 mb-4">
                <Button
                  onClick={() => document.getElementById('camera-input').click()}
                  size="sm"
                  className="bg-[#C9A961]/10 text-[#C9A961] hover:bg-[#C9A961]/20 border border-[#C9A961]/30"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Take Photo
                </Button>
                <Button
                  onClick={() => document.getElementById('photo-input').click()}
                  size="sm"
                  variant="outline"
                  className="border-[#C9A961]/30 text-[#C9A961] hover:bg-[#C9A961]/10"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
                <input
                  id="camera-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  multiple
                />
                <input
                  id="photo-input"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  multiple
                />
              </div>

              {photos.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-12 w-12 text-[#C9A961]/20 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No photos yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo.url}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded border border-[#C9A961]/20 group-hover:border-[#C9A961]/50 transition-all"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div>
              <div className="mb-4">
                <Button
                  onClick={() => document.getElementById('document-input').click()}
                  size="sm"
                  className="bg-[#C9A961]/10 text-[#C9A961] hover:bg-[#C9A961]/20 border border-[#C9A961]/30"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Documents
                </Button>
                <input
                  id="document-input"
                  type="file"
                  onChange={handleDocumentUpload}
                  className="hidden"
                  multiple
                />
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-12">
                  <File className="h-12 w-12 text-[#C9A961]/20 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No documents yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc, index) => (
                    <div key={index} className="bg-black/30 p-3 rounded border border-[#C9A961]/20 hover:border-[#C9A961]/40 transition-all flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <File className="h-5 w-5 text-[#C9A961]" />
                        <div>
                          <p className="text-white text-sm font-medium">{doc.name}</p>
                          <p className="text-gray-500 text-xs">{doc.size}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#C9A961] hover:bg-[#C9A961]/10 h-7 w-7 p-0"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#C9A961] hover:bg-[#C9A961]/10 h-7 w-7 p-0"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileGalleryFullScreenNew;